import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProjectCard from './components/ProjectCard';
import BannerBeCreative from './Assets/banner_BeCreative_sm.jpg';
import ProfilePicture from './Assets/RobertWMassey.jpg';
import WeatherAftermathTrackerPreview from './Assets/Weather-Aftermath-Tracker-preview.jpg';
import './App.css';
import { Container, Box, Tabs, Tab, Typography, Paper, Divider } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function App() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/portfolio-projects`, {
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        });
        setProjects(response.data);
        setLoading(false);
      } catch (err) {
        console.error('API Error:', err.response?.data || err.message);
        setError('Failed to fetch projects. Please try again later.');
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f6fa', minHeight: '100vh' }}>
      {/* Banner */}
      <Box sx={{ width: '100%', bgcolor: 'white', boxShadow: 1 }}>
        <Container maxWidth="md" disableGutters sx={{ position: 'relative' }}>
          <Box sx={{ width: '100%', height: { xs: 200, md: 350 }, overflow: 'hidden', borderRadius: 2 }}>
            <img
              src={BannerBeCreative}
              alt="Be Creative Banner"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
          {/* Profile Picture - Bottom Left Overlapping */}
          <Box
            sx={{
              position: 'absolute',
              left: { xs: 24, md: 56 },
              bottom: -100,
              zIndex: 10,
              width: { xs: 120, md: 168 },
              height: { xs: 120, md: 168 },
              borderRadius: '50%',
              border: '4px solid white',
              boxShadow: 3,
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={ProfilePicture}
              alt="Robert W. Massey"
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          </Box>
        </Container>
      </Box>

      {/* Profile Info and Tabs */}
      <Container maxWidth="xl" sx={{ mt: 10 }}>
        <Paper elevation={1} sx={{ maxWidth: '900px', margin: '0 auto', p: { xs: 2, md: 4 }, pt: 8, mt: 0, mb: 3, textAlign: 'left', position: 'relative' }}>
          <Box sx={{ mt: { xs: 8, md: 0 }, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color="#384E77" gutterBottom>
              Professor Robert W. Massey
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
              Software Developer Portfolio
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              A curated collection of my personal creative and technical work.
            </Typography>
          </Box>
          {/* Tabs */}
          <Box sx={{ mt: 4 }}>
            <Tabs value={tab} onChange={handleTabChange} centered>
              <Tab label="Projects" />
              <Tab label="About" />
              <Tab label="Contact" />
            </Tabs>
            <Divider sx={{ mb: 2, bgcolor: '#384E77', height: 3, opacity: 0.2 }} />
          </Box>

          {/* Tab Content: Projects or About */}
          {tab === 0 && (
            <Box sx={{ mt: 2, border: '3px solid blue', maxWidth: '900px', margin: '0 auto', pb: 6, minHeight: 400 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr',
                  },
                  justifyItems: 'center',
                }}
              >
                {[...projects].reverse().map((project) => (
                  <ProjectCard
                    key={project.id}
                    name={project.name}
                    description={project.description}
                    url={project.url}
                    homepage={project.homepage}
                    topics={project.topics}
                    stars={project.stars}
                    language={project.language}
                    updatedAt={project.updatedAt}
                    screenshot={project.name === 'Weather-Aftermath-Tracker' ? WeatherAftermathTrackerPreview : undefined}
                  />
                ))}
              </Box>
            </Box>
          )}
          {tab === 1 && (
            <Box sx={{ mt: 4, maxWidth: 700, mx: 'auto', p: { xs: 2, md: 4 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h5" fontWeight={700} color="#384E77" gutterBottom>
                Robert Massey
              </Typography>
              <Divider sx={{ mb: 2, bgcolor: '#384E77', height: 3, opacity: 0.2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                903-436-3353 | rwmasseysus@gmail.com | X: @RobertWMassey | www.robertwmassey.com
              </Typography>
              <Typography variant="body1" color="text.primary" paragraph>
                Robert Massey is an accomplished Lead Developer Architect and Adjunct Computer Science Professor with over three decades of experience in IT and software engineering, data analytics, and business intelligence. He holds a Master of Information Systems (2010) and a Bachelor of Science in Information Technology/Software Engineering (2008) from the University of Phoenix, complemented by an Associate Degree in Computer Science from Grayson College (1996).
              </Typography>
              <Typography variant="body1" color="text.primary" paragraph>
                Currently, Mr. Massey serves as a Lead Developer Architect middleware, data services and Reporting and Data integrations Senior Team Lead at ATOS Inc. (2010–present), where he spearheads Paragon, CERNER, EPIC EMR development, designs C#, C++, Python applications on Windows based and Linux systems, and has managed a global team of 21 developers. His expertise extends to disaster recovery transitions, SQL scheduler software projects in C# Converted into agentic AI services using Microsoft Autogen, and leveraging tools like SSIS, SSRS, and KRONOS Analytics for productivity analysis. Concurrently, as an Adjunct Professor at Grayson College (2008–present), he instructs students in computer science, programming, game design, and database design. His prior roles include contractor positions at McKesson Inc., Wilson N. Jones Hospital, UHS (Texoma Medical Center) and Millennia Information Systems, as well as a Systems Analyst/Software Engineer II, where he contributed to EMR implementations and developed web applications such as a hospitalist tracker and a ticket POS system for Reba McEntire's hospital benefit concerts through Texoma Medical Center at Choctaw concerts in Durant Oklahoma.
              </Typography>
              <Typography variant="body1" color="text.primary" paragraph>
                Proficient in programming languages (C#, C++, Python, SQL, etc.), databases (MS SQL Server, Oracle DBMS), and EMR systems (EPIC, CERNER certified, McKesson Paragon), Cloverleaf level 1 certified Data integrations, Mr. Massey also excels in machine learning, deep learning, and web development. His certifications include Microsoft Developer (2005), Cloverleaf level 1, CERNER, Crystal Report Developer, NOAA Storm Spotter, and FEMA CERT.
              </Typography>
              <Typography variant="body1" color="text.primary" paragraph>
                Beyond his professional pursuits, Mr. Massey is passionate about weather, machine learning, developing predictive models with TensorFlow and Microsoft Autogen, and exploring agentic AI solutions. He builds holographic projection systems for educational purposes, contributes to storm chasing as a certified spotter and HAM radio operator (KF5FLN), and engages in scouting leadership. A multi-instrumentalist, he enjoys playing guitar, saxophone, and other instruments across blues, jazz, and rock genres.
              </Typography>
            </Box>
          )}
          {/* Contact Section (under Contact tab) */}
          {tab === 2 && (
            <Box sx={{ mt: 4, maxWidth: 700, mx: 'auto', p: { xs: 2, md: 4 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h5" fontWeight={700} color="#384E77" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2, bgcolor: '#384E77', height: 3, opacity: 0.2 }} />
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li>
                  <Typography variant="body1" color="text.primary">Name: Robert Massey</Typography>
                </li>
                <li>
                  <Typography variant="body1" color="text.primary">Phone: 903-436-3353</Typography>
                </li>
                <li>
                  <Typography variant="body1" color="text.primary">
                    Email: <a href="mailto:rwmasseysus@gmail.com" style={{ color: '#1976d2', textDecoration: 'underline' }}>rwmasseysus@gmail.com</a>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" color="text.primary">
                    X: <a href="https://x.com/RobertWMassey" target="_blank" rel="noopener noreferrer" style={{ color: '#1da1f2', textDecoration: 'underline' }}>@RobertWMassey</a>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" color="text.primary">
                    Website: <a href="https://www.robertwmassey.com" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>www.robertwmassey.com</a>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" color="text.primary">
                    LinkedIn: <a href="https://www.linkedin.com/in/robert-massey-4626b619/" target="_blank" rel="noopener noreferrer" style={{ color: '#0077b5', textDecoration: 'underline' }}>
                      Robert Massey's Profile
                    </a>
                  </Typography>
                </li>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default App;
